import { useCallback, useState } from 'react';

import { backupSettingsClient, levelValue } from '../api';
import { useBackupChoice, useBackupSave, useCustomerBackups } from '../hooks';

import BackupsSetting from './BackupsSetting';
import { backupSavedToast } from './toast';

import SurgeToast, {
  SurgeToastMessage,
} from '@/routes/Agency/PaySurge/components/SurgeToast';

type Props = { customerId: number | string };

/**
 * The client's auto backups setting, in the client modal's Rates tab beside
 * surge pay. Clients are opt-in: off until someone turns it on here. Saved on its own endpoint the moment its warning is confirmed,
 * not with the modal's Save: the warning IS the confirmation, and holding the
 * change for Save would let Cancel quietly drop a choice the admin confirmed.
 */
const ClientBackupsSetting = ({ customerId }: Props) => {
  const { data, isError } = useCustomerBackups(customerId);
  const [toast, setToast] = useState<SurgeToastMessage | null>(null);
  const dismiss = useCallback(() => setToast(null), []);

  const { save, saving, error } = useBackupSave(
    (enabled) =>
      backupSettingsClient.setCustomer(customerId, enabled as boolean),
    'Could not save auto backups. Please try again.',
    (saved) => setToast(backupSavedToast('client', true, saved.enabled)),
  );
  const chooser = useBackupChoice(save);

  return (
    <div
      className="dashv2-scope dashv2-bkpset-block"
      data-testid="client-backups-section"
    >
      {isError ? (
        <p className="dashv2-bkpset-error" role="alert">
          Could not load auto backups. Close and try again.
        </p>
      ) : data ? (
        <>
          <BackupsSetting
            level="client"
            label="Auto backups for this client"
            {...levelValue('client', { client: data.enabled })}
            switchOn={data.switch_on}
            saving={saving}
            error={error}
            chooser={chooser}
            testId="client-backups"
          />
          <p className="dashv2-bkpset-help">
            {data.enabled
              ? 'On — GravyWork invites backups at the 3-hour check when hired workers haven’t confirmed, and the earliest standby is hired when a spot opens.'
              : 'Off — clients are opt-in. No auto backups or standby on this client’s shifts, unless an order or job is turned on.'}
          </p>
        </>
      ) : null}
      <SurgeToast toast={toast} onDismiss={dismiss} />
    </div>
  );
};

export default ClientBackupsSetting;
